import { Doc } from '@dadohr/shared/domainTypes/document';
import { IOrganization } from '@dadohr/shared/domainTypes/org';
import { IUser } from '@dadohr/shared/domainTypes/user';
import styled from '@emotion/styled';
import { Button, Radio, Typography } from '@material-ui/core';
import { AlertTriangle } from 'lucide-react';
import React, { useMemo, useState } from 'react';
import { ButtonWithPromise } from '../../../../components/ButtonWithPromise';
import { DataGridItem } from '../../../../components/DataGrid';
import { InfoBox } from '../../../../components/InfoBox';
import { PageLoader } from '../../../../components/Loader';
import { NotFound } from '../../../../components/NotFound';
import { useRouter } from '../../../../hooks/useRouter';
import { useSnackbar } from '../../../../hooks/useSnackbar';
import { UnsafeTFunction, useUnsafeTranslation } from '../../../../i18next';
import { FlexContainer } from '../../../../layout/Flex';
import { PageBody } from '../../../../layout/PageBody';
import { Section } from '../../../../layout/Section';
import { useRoutes } from '../../../../routes';
import { useFirebaseFunction } from '../../../../services/firebaseFunctions';
import { timestampToMillisSafe } from '../../../../services/time';
import { toFullName, useUserById } from '../../../../services/user';
import { useAllUserDataGridFields } from '../UserDataGrid';
import {
  buildResolvedFields,
  ClashField,
  computeClashes,
  isDisplayableClash,
  isEmptyValue,
  MergeSide
} from './clashes';
import { MergeStepper } from './MergeStepper';
import { MergingModal } from './MergingModal';
import { getMergeErrorMessage, mergeEmployees } from './service';

// 3-column layout: Field label | record A | record B. Shared by the header row
// and every clash row so the columns line up.
const ROW_COLUMNS = 'minmax(150px, 190px) 1fr 1fr';

const HeaderRow = styled('div')((p) => ({
  display: 'grid',
  gridTemplateColumns: ROW_COLUMNS,
  columnGap: p.theme.spacing(3),
  alignItems: 'baseline',
  paddingBottom: p.theme.spacing(1.5),
  borderBottom: `1px solid ${p.theme.custom.colors.gray.w200}`
}));

const Row = styled('div')((p) => ({
  display: 'grid',
  gridTemplateColumns: ROW_COLUMNS,
  columnGap: p.theme.spacing(3),
  alignItems: 'stretch',
  padding: `${p.theme.spacing(3)}px 0`,
  borderBottom: `1px solid ${p.theme.custom.colors.gray.w200}`
}));

const HeaderRecord = styled('div')({
  '& em': { fontStyle: 'normal', fontWeight: 200 }
});

const FieldCell = styled('div')({
  display: 'flex',
  alignItems: 'center'
});

const OptionCard = styled('div')<{ selected: boolean }>((p) => ({
  display: 'flex',
  alignItems: 'flex-start',
  gap: p.theme.spacing(1),
  width: '100%',
  height: '100%',
  boxSizing: 'border-box',
  cursor: 'pointer',
  padding: `${p.theme.spacing(1.5)}px ${p.theme.spacing(2)}px`,
  borderRadius: p.theme.custom.border.radius(2),
  border: `1px solid ${
    p.selected
      ? p.theme.custom.colors.blue.w500
      : p.theme.custom.colors.gray.w300
  }`,
  background: p.selected
    ? p.theme.custom.colors.blue.w100
    : p.theme.custom.colors.gray.white,
  transition: 'border-color 120ms ease, background 120ms ease'
}));

const CardRadio = styled(Radio)({
  padding: 0,
  marginTop: 2
});

const CardValue = styled('div')({
  wordBreak: 'break-word'
});

const NoValue = styled('span')((p) => ({
  color: p.theme.custom.colors.gray.w500,
  fontStyle: 'italic'
}));

const WarningBanner = styled(InfoBox)((p) => ({
  display: 'flex',
  alignItems: 'flex-start',
  gap: p.theme.spacing(1.5),
  marginTop: p.theme.spacing(4)
}));

// Reserve space below the content so the last clash rows can scroll clear of
// the sticky footer instead of being hidden behind it.
const ContentArea = styled('div')((p) => ({
  paddingBottom: p.theme.spacing(12)
}));

const Footer = styled('div')((p) => ({
  position: 'sticky',
  bottom: 0,
  zIndex: 2,
  background: p.theme.custom.colors.background.main,
  borderTop: p.theme.custom.border.standard,
  // Floats above the scrolling list; the shadow makes "content scrolls behind
  // this bar" read as intentional rather than a clipped overlap.
  boxShadow: '0 -4px 12px rgba(0, 0, 0, 0.06)',
  padding: `${p.theme.spacing(2)}px 0`,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between'
}));

type GridFieldFn = (
  d: Doc<IUser>,
  t: UnsafeTFunction,
  df: IOrganization['dateFormats']
) => DataGridItem | null;

/** "middleName" -> "Middle Name", "greenhouseId" -> "Greenhouse Id". */
const humanizeKey = (key: string): string => {
  const spaced = key
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replace(/[_-]+/g, ' ')
    .trim();
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
};

export const MergeClashResolution = ({
  profileUser,
  org,
  otherUserId,
  onCancel
}: {
  profileUser: Doc<IUser>;
  org: IOrganization;
  otherUserId: string;
  onCancel: () => void;
}) => {
  const { t } = useUnsafeTranslation();
  const { ROUTES } = useRoutes();
  const { history } = useRouter();
  const { enqueueSnackbarSuccess, enqueueSnackbarError } = useSnackbar();

  const [otherUser, loadingOther, errorOther] = useUserById(otherUserId);
  const [selections, setSelections] = useState<Record<string, MergeSide>>({});
  const [merging, setMerging] = useState(false);

  // The backend keeps the OLDER record (by Firestore create-time; ties resolve
  // to the initiating record). Fetch both create-times so we can show which
  // record survives before the merge runs.
  const [createProfile, loadingCreateProfile] = useFirebaseFunction<{
    createTime: unknown;
  }>('user-getAdditionalUserData', { userId: profileUser.id, orgId: org.id }, [
    profileUser.id,
    org.id
  ]);
  const [createOther, loadingCreateOther] = useFirebaseFunction<{
    createTime: unknown;
  }>('user-getAdditionalUserData', { userId: otherUserId, orgId: org.id }, [
    otherUserId,
    org.id
  ]);

  const profileMs = timestampToMillisSafe(createProfile?.createTime);
  const otherMs = timestampToMillisSafe(createOther?.createTime);
  // Other survives only when strictly older and both times are known;
  // otherwise the initiating (profile) record is kept (matches the tie-break).
  const keptSide: MergeSide =
    otherMs != null && profileMs != null && otherMs < profileMs
      ? 'other'
      : 'profile';

  const gridFields = useAllUserDataGridFields(org.customUserFields);

  // `clashes` includes one-sided differences (kept out of the UI) so their set
  // value still carries into the payload; `visibleClashes` are the true
  // conflicts the admin resolves on screen.
  const clashes = useMemo<ClashField[]>(
    () =>
      otherUser
        ? computeClashes(profileUser, otherUser, org.customUserFields, keptSide)
        : [],
    [profileUser, otherUser, org.customUserFields, keptSide]
  );
  const visibleClashes = useMemo(
    () => clashes.filter(isDisplayableClash),
    [clashes]
  );

  // Wait for create-times too, so the kept/deleted columns don't flip after
  // the initial render.
  if (loadingOther || loadingCreateProfile || loadingCreateOther) {
    return <PageLoader />;
  }
  if (errorOther || !otherUser) {
    return <NotFound />;
  }

  const deletedSide: MergeSide = keptSide === 'profile' ? 'other' : 'profile';
  const keptUser = keptSide === 'profile' ? profileUser : otherUser;
  const deletedUser = keptSide === 'profile' ? otherUser : profileUser;

  const effectiveSide = (c: ClashField): MergeSide =>
    selections[c.key] || c.defaultSide;

  // Label + value rendering both come from the shared data-grid field map, so
  // the merge screen reads exactly like the Profile Data tab.
  const labelFor = (c: ClashField): string => {
    const gf = (gridFields as Record<string, GridFieldFn>)[c.key];
    const item = gf?.(keptUser, t, org.dateFormats);
    if (item?.label) {
      return item.label as string;
    }
    // Field with no grid renderer (e.g. gender/active) or a custom key with no
    // org definition — humanize the bare key.
    return humanizeKey(
      c.key.startsWith('customFields.')
        ? c.key.replace('customFields.', '')
        : c.key
    );
  };

  const renderValue = (c: ClashField, side: MergeSide): React.ReactNode => {
    const raw = side === 'profile' ? c.valueProfile : c.valueOther;
    if (isEmptyValue(c.type, raw)) {
      return <NoValue>{t('No value')}</NoValue>;
    }
    const userDoc = side === 'profile' ? profileUser : otherUser;
    const gf = (gridFields as Record<string, GridFieldFn>)[c.key];
    if (gf) {
      const item = gf(userDoc, t, org.dateFormats);
      if (item && item.d !== '' && item.d != null) {
        return item.d;
      }
    }
    if (typeof raw === 'boolean') {
      return raw ? t('Yes') : t('No');
    }
    return String(raw);
  };

  const handleMerge = async () => {
    setMerging(true);
    try {
      // Full clash list: one-sided fields carry their set value via defaultSide.
      const resolvedFields = buildResolvedFields(clashes, selections);
      const res = await mergeEmployees({
        orgId: org.id,
        userAId: profileUser.id,
        userBId: otherUser.id,
        initiatedFromUserId: profileUser.id,
        resolvedFields
      });
      enqueueSnackbarSuccess(t('Employees merged'));
      // The backend decides which record survives — navigate to whichever it
      // kept, which may not be the profile we started on.
      history.push(ROUTES.users.profile.url(res.keptUserId));
    } catch (err) {
      setMerging(false);
      enqueueSnackbarError(getMergeErrorMessage(err));
    }
  };

  const keptName = toFullName(keptUser.data);
  const deletedName = toFullName(deletedUser.data);

  return (
    <PageBody noTopPadding>
      <Section>
        <ContentArea>
          <Typography component="h1" variant="h1">
            {t('Merge employees')}
          </Typography>
          <div style={{ marginTop: 16, marginBottom: 24 }}>
            <MergeStepper />
          </div>

          <Typography component="h2" variant="h3">
            {t('Data clashes')}
          </Typography>
          <Typography variant="body2" color="textSecondary">
            {visibleClashes.length === 0
              ? t('These records have no conflicting fields.')
              : t(
                  '{{ count }} fields hold different values on the two records. Pick the value to keep on the merged profile.',
                  { count: visibleClashes.length }
                )}
          </Typography>

          {visibleClashes.length > 0 && (
            <div style={{ marginTop: 24 }}>
              <HeaderRow>
                <Typography variant="h6" color="textSecondary">
                  {t('Field')}
                </Typography>
                <HeaderRecord>
                  <Typography variant="h6" color="textSecondary">
                    {keptName} · <em>{keptUser.data.email}</em>
                  </Typography>
                </HeaderRecord>
                <HeaderRecord>
                  <Typography variant="h6" color="textSecondary">
                    {deletedName} · <em>{deletedUser.data.email}</em>
                  </Typography>
                </HeaderRecord>
              </HeaderRow>

              {visibleClashes.map((c) => {
                const selected = effectiveSide(c);
                const renderOption = (side: MergeSide) => (
                  <OptionCard
                    selected={selected === side}
                    onClick={() =>
                      setSelections((prev) => ({ ...prev, [c.key]: side }))
                    }
                  >
                    <CardRadio
                      checked={selected === side}
                      color="primary"
                      size="small"
                      onChange={() =>
                        setSelections((prev) => ({ ...prev, [c.key]: side }))
                      }
                    />
                    <CardValue>
                      <Typography variant="body1">
                        {renderValue(c, side)}
                      </Typography>
                    </CardValue>
                  </OptionCard>
                );
                return (
                  <Row key={c.key}>
                    <FieldCell>
                      <Typography variant="h6">{labelFor(c)}</Typography>
                    </FieldCell>
                    {renderOption(keptSide)}
                    {renderOption(deletedSide)}
                  </Row>
                );
              })}
            </div>
          )}

          <WarningBanner color="yellow">
            <AlertTriangle size={18} style={{ flexShrink: 0, marginTop: 2 }} />
            <Typography variant="body2">
              {t(
                'Experiences and moments from both user profiles move onto the merged profile. A merge cannot be undone.'
              )}
            </Typography>
          </WarningBanner>
        </ContentArea>

        <Footer>
          <Typography variant="body2" color="textSecondary">
            {t('{{ resolved }} of {{ total }} clashes resolved', {
              resolved: visibleClashes.length,
              total: visibleClashes.length
            })}
          </Typography>
          <FlexContainer alignItems="center" style={{ gap: 16 }}>
            <Button color="primary" onClick={onCancel} disabled={merging}>
              {t('Cancel')}
            </Button>
            <ButtonWithPromise
              variant="contained"
              color="primary"
              onClick={handleMerge}
              pending={`${t('Merging')}...`}
            >
              {t('Merge employees')}
            </ButtonWithPromise>
          </FlexContainer>
        </Footer>
      </Section>

      <MergingModal open={merging} />
    </PageBody>
  );
};
